import React, { useMemo } from "react";
import { AHIcon, AHText, Box, useTheme, useTime } from "arkh";
import { graphql, useFragment } from "react-relay";

import { useNowIndexes } from "../TeamSchedule/useNowIndexes";
import { TeamScheduleListItem } from "../TeamScheduleListItem/TeamScheduleListItem";
import { DateTime } from "luxon";
import { TeamScheduleSectionHeaderSummary } from "../TeamSchedule/TeamScheduleHeader/TeamScheduleSectionHeader";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { Image, Pressable, View } from "react-native";
import { FormattedMessage } from "react-intl";
import { TeamTeamSchedulePreview_team$key } from "./__generated__/TeamTeamSchedulePreview_team.graphql";
import { isDarkColor } from "../TeamHome/isDarkColor";
import { getTeamScheduleListItemHeight } from "../TeamScheduleListItem/getTeamScheduleListItemHeight";

export const TeamTeamSchedulePreview = React.memo(TeamTeamSchedulePreviewC);
function TeamTeamSchedulePreviewC(props: {
  team: TeamTeamSchedulePreview_team$key;
  onPress: (freq: "DAILY" | "WEEKLY" | "MONTHLY") => void;
  onOpenAdmin: () => void;
  hasAdminAccess: boolean;
}) {
  const team = useFragment(
    graphql`
      fragment TeamTeamSchedulePreview_team on Team {
        id
        color
        dailySchedule: currentTeamSchedule(freq: DAILY) {
          ...TeamScheduleSectionHeaderSummary_teamSchedule
          events(includeInlineRoutines: true) {
            startAt
            id
            __typename
            ... on RoutineOccasion {
              routine {
                description
                descriptionImages {
                  __typename
                }
              }
              assigned {
                id
              }
              assignedRoles {
                id
              }
            }
            ... on TeamScheduleInlineRoutineOccasion {
              taskExecution {
                routineOccasion {
                  routine {
                    description
                    descriptionImages {
                      __typename
                    }
                  }
                  assigned {
                    id
                  }
                  assignedRoles {
                    id
                  }
                }
              }
            }
            ...TeamScheduleListItem_TeamScheduleEventOccasion
          }
        }
        weeklySchedule: currentTeamSchedule(freq: WEEKLY) {
          ...TeamScheduleSectionHeaderSummary_teamSchedule
          events(includeInlineRoutines: true) {
            id
          }
        }
        monthlySchedule: currentTeamSchedule(freq: MONTHLY) {
          ...TeamScheduleSectionHeaderSummary_teamSchedule
          events(includeInlineRoutines: true) {
            id
          }
        }
      }
    `,
    props.team
  );

  const theme = useTheme();
  const now = useTime();

  const events = team.dailySchedule?.events.map((e, i) => ({
    index: i,
    startAt: e.startAt,
    event: e,
  }));
  const [nowIndexes, scrollIndex] = useNowIndexes({
    now,
    events: events ?? [],
  });

  const eventsToShow = useMemo(() => {
    if (events.length < 3) {
      return events;
    }
    if (scrollIndex >= 0 && scrollIndex + 3 <= events.length) {
      return events.slice(scrollIndex, scrollIndex + 3);
    }

    return events.slice(-3);
  }, [events, scrollIndex]);

  const intl_token = useIntlTokens();
  const isDarkTint = isDarkColor(team.color ?? theme.primary);

  const showTeamScheduleEmpty = useMemo(
    () =>
      team.dailySchedule.events.length === 0 &&
      team.weeklySchedule.events.length === 0 &&
      team.monthlySchedule.events.length === 0 &&
      props.hasAdminAccess,
    [
      props.hasAdminAccess,
      team.dailySchedule.events.length,
      team.monthlySchedule.events.length,
      team.weeklySchedule.events.length,
    ]
  );

  return (
    <Box spaceTop="small">
      <Box spaceHorizontal="medium">
        {team.monthlySchedule.events.length > 0 ? (
          <TeamScheduleSectionHeaderSummary
            title={intl_token.MonthlyRoutines}
            onPress={() => {
              props.onPress("MONTHLY");
            }}
            isFree
            teamSchedule={team.monthlySchedule}
          />
        ) : null}
        {team.weeklySchedule.events.length > 0 ? (
          <TeamScheduleSectionHeaderSummary
            title={intl_token.WeeklyRoutines}
            onPress={() => {
              props.onPress("WEEKLY");
            }}
            isFree
            teamSchedule={team.weeklySchedule}
          />
        ) : null}
        {team.dailySchedule.events.length > 0 ? (
          <TeamScheduleSectionHeaderSummary
            title={intl_token.DailyRoutines}
            onPress={() => {
              props.onPress("DAILY");
            }}
            teamSchedule={team.dailySchedule}
          />
        ) : null}
      </Box>
      {eventsToShow.map(({ event, index, startAt }, position) => {
        const status = () => {
          if (nowIndexes.includes(index)) {
            return "active";
          }
          if (
            nowIndexes.includes(index - 1) &&
            DateTime.fromISO(event.startAt) < now.plus({ hour: 1 })
          ) {
            return "soon";
          }

          return "none";
        };

        const occasion =
          event.__typename === "TeamScheduleInlineRoutineOccasion"
            ? event.taskExecution?.routineOccasion
            : event;

        const hasDescription =
          (occasion?.routine?.description?.length ?? 0) > 0;
        const hasDescriptionMedia =
          (occasion?.routine?.descriptionImages?.length ?? 0) > 0;
        const hasAssignedEmployees = (occasion?.assigned?.length ?? 0) > 0;
        const hasAssignedRoles = (occasion?.assignedRoles?.length ?? 0) > 0;

        const height = getTeamScheduleListItemHeight(
          "DAILY",
          (hasAssignedEmployees || hasAssignedRoles) &&
            (hasDescription || hasDescriptionMedia)
        );

        return (
          <Box style={{ height }} key={event.id}>
            <TeamScheduleListItem
              eventOccasion={event}
              isFirst={false}
              isLast={position === eventsToShow.length - 1}
              itemType="timeOfDay"
              status={status()}
              teamColor={team.color ?? theme.primary}
            />
          </Box>
        );
      })}
      {showTeamScheduleEmpty ? <TeamScheduleEmpty /> : null}
      {team.dailySchedule.events.length > 0 || showTeamScheduleEmpty ? (
        <View style={{ marginBottom: 20 }}>
          <View
            style={{
              position: "absolute",
              bottom: -20,
              left: 0,
              right: 0,
            }}
          >
            <Pressable
              onPress={() => {
                if (showTeamScheduleEmpty) {
                  props.onOpenAdmin();
                } else {
                  props.onPress("DAILY");
                }
              }}
              style={({ pressed }) => ({
                alignSelf: "center",
                height: 30,
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <Box
                spaceHorizontal="medium"
                style={{
                  backgroundColor: team.color ?? theme.primary,
                  alignItems: "center",
                  flexDirection: "row",
                  flex: 1,
                  borderRadius: 20,
                }}
              >
                <AHText
                  type="subtitle"
                  textAlign="center"
                  numberOfLines={1}
                  color={isDarkTint ? "text-inverted" : "text-primary"}
                >
                  {showTeamScheduleEmpty ? (
                    <FormattedMessage
                      defaultMessage="Get started with routines"
                      description="TeamHomeDailySchedule Get started with routines"
                    />
                  ) : (
                    <FormattedMessage
                      defaultMessage="Show all routines"
                      description="TeamHomeDailySchedule Show full schedule"
                    />
                  )}
                </AHText>
                <AHIcon
                  spaceLeft="small"
                  type="arrow-right"
                  color={isDarkTint ? "text-inverted" : "text-primary"}
                />
              </Box>
            </Pressable>
          </View>
        </View>
      ) : null}
    </Box>
  );
}

const TeamScheduleEmptyCopy = {
  title: (
    <FormattedMessage
      defaultMessage="Routines"
      description="RoutinesScheduleEmpty Title"
    />
  ),
  subtitle: (
    <FormattedMessage
      defaultMessage="Organise daily, weekly and monthly routines here."
      description="RoutinesScheduleEmpty Subtitle"
    />
  ),
};

function TeamScheduleEmpty() {
  return (
    <Box spaceHorizontal="medium">
      <Box color="surface" space="medium" cornerRadius="large">
        <AHText type="title-small">{TeamScheduleEmptyCopy.title}</AHText>
        <AHText type="list-item-title" spaceTop="small">
          {TeamScheduleEmptyCopy.subtitle}
        </AHText>
        <Box spaceBottom="medium">
          <Image
            style={{
              width: "100%",
              height: 180,
            }}
            resizeMode="contain"
            source={require("../TeamHome/teamScheduleCart.png")}
          />
        </Box>
      </Box>
    </Box>
  );
}
